import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  ChatStore,
  ChatState,
  ChatTarget,
  ChatType,
  ChatMemberDisplay,
  SENDABLE_ASSIGNMENT_STATUSES,
  CombinedMessage,
} from '@th-types/chat.type';
import IMessageData, { ISystemMessageData } from '@th-types/message.type';
import { IChatMember } from '@th-types/person.type';
import IDirectMessage from '@th-types/direct-message.type';
import IDirectMessageChat from '@th-types/direct-messages-chat.type';
import { fetchChatMessages, sendChatMessage } from '@services/chat/api';
import {
  fetchDirectChatMessages,
  sendDirectChatMessage,
} from '@services/directChat/api';
import {
  DIRECT_CHAT_MESSAGE_PAGE_SIZE,
  hasMoreOlderMessages,
  nextOlderMessagesOffset,
} from '@utils/directChatPagination';
import { isAnnouncementChat } from '@constants/directChat';

function toChatMemberDisplay(member: IChatMember): ChatMemberDisplay {
  return {
    id: member.id,
    fullName: member.fullName,
    profileImageUrl: member.profileImageUrl,
    type: member.type,
  };
}

function mapDirectMessages(dms: IDirectMessage[]): IMessageData[] {
  return dms.map((dm) => ({
    id: dm.id,
    authorId: dm.author?.id ?? 0,
    created: dm.dateCreated,
    text: dm.text,
    jobId: 0,
    readBy: [],
    sent: dm.sent ?? true,
  }));
}

function collectDirectChatMembers(
  response: IDirectMessageChat
): ChatMemberDisplay[] {
  const membersMap = new Map<number, ChatMemberDisplay>();
  (response.messages || []).forEach((dm) => {
    if (dm.author) {
      membersMap.set(dm.author.id, toChatMemberDisplay(dm.author));
    }
  });
  if (response.me) {
    membersMap.set(response.me.id, toChatMemberDisplay(response.me));
  }
  if (response.worker) {
    membersMap.set(response.worker.id, {
      id: response.worker.id,
      fullName: response.worker.fullName,
      profileImageUrl: response.worker.profileImageUrl,
    });
  }
  return Array.from(membersMap.values());
}

function mergeChatMembers(
  existing: ChatMemberDisplay[],
  incoming: ChatMemberDisplay[]
): ChatMemberDisplay[] {
  const membersMap = new Map<number, ChatMemberDisplay>();
  existing.forEach((m) => membersMap.set(m.id, m));
  incoming.forEach((m) => membersMap.set(m.id, m));
  return Array.from(membersMap.values());
}

export const initialState: ChatState = {
  isOpen: false,
  mode: 'drawer',
  activeChat: null,
  currentUserId: null,
  initialFirstUnreadMessageId: null,
  messages: [],
  systemMessages: [],
  chatMembers: [],
  isLoading: false,
  isSending: false,
  error: null,
  isSendingMsgAllowed: true,
  pagination: null,
  hasMoreOlder: false,
  isLoadingOlder: false,
};

export function combineAndSortMessages(
  messages: IMessageData[],
  systemMessages: ISystemMessageData[]
): CombinedMessage[] {
  const combined: CombinedMessage[] = [
    ...messages.map((msg) => ({ ...msg, type: 'message' as const })),
    ...systemMessages.map((msg) => ({
      ...msg,
      systemType: msg.type,
      type: 'system' as const,
    })),
  ];

  return combined.sort((a, b) => {
    const dateA = new Date(a.created).getTime();
    const dateB = new Date(b.created).getTime();
    return dateA - dateB;
  });
}

function canSendMessages(status?: string): boolean {
  if (!status) return true; // Default to true if no status (allows sending)
  return SENDABLE_ASSIGNMENT_STATUSES.includes(
    status as (typeof SENDABLE_ASSIGNMENT_STATUSES)[number]
  );
}

let currentLoadToken = 0;

function startNewLoad(): number {
  currentLoadToken += 1;
  return currentLoadToken;
}

function isStaleLoad(token: number): boolean {
  return token !== currentLoadToken;
}

async function loadMessagesFromApi(
  target: ChatTarget,
  set: (partial: Partial<ChatState>) => void,
  token: number
): Promise<void> {
  try {
    if (target.chatType === ChatType.DIRECT) {
      const response = await fetchDirectChatMessages(target.jobAssignmentId, {
        max: DIRECT_CHAT_MESSAGE_PAGE_SIZE,
        offset: 0,
      });

      if (isStaleLoad(token)) return;

      const pageMessages = response.messages || [];
      const messages = mapDirectMessages(pageMessages);
      const pagination = response.pagination ?? null;

      const isAnnouncement = isAnnouncementChat({
        isAnnouncement: target.isAnnouncement,
        type: response.type,
      });

      set({
        messages,
        systemMessages: [],
        chatMembers: collectDirectChatMembers(response),
        isLoading: false,
        isSendingMsgAllowed: !isAnnouncement,
        pagination,
        hasMoreOlder: hasMoreOlderMessages(pagination, pageMessages.length),
        isLoadingOlder: false,
        activeChat: { ...target, isAnnouncement },
      });
    } else {
      const response = await fetchChatMessages(target.jobAssignmentId);

      if (isStaleLoad(token)) return;

      const isSendingAllowed = canSendMessages(response.jobAssignment?.status);

      set({
        messages: response.messages || [],
        systemMessages: response.systemMessages || [],
        chatMembers: (response.chatMembers || []).map(toChatMemberDisplay),
        isLoading: false,
        isSendingMsgAllowed: isSendingAllowed,
        pagination: null,
        hasMoreOlder: false,
        isLoadingOlder: false,
      });
    }
  } catch (error) {
    if (isStaleLoad(token)) return;

    const errorMessage =
      error instanceof Error ? error.message : 'Failed to load messages';

    set({
      error: errorMessage,
      isLoading: false,
      isSendingMsgAllowed: false,
      pagination: null,
      hasMoreOlder: false,
      isLoadingOlder: false,
    });
  }
}

const paginationReset = {
  pagination: null as ChatState['pagination'],
  hasMoreOlder: false,
  isLoadingOlder: false,
};

export const useChatStore = create<ChatStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      openChat: async (target: ChatTarget, currentUserId?: number) => {
        const token = startNewLoad();
        set({
          isOpen: true,
          mode: 'drawer',
          activeChat: target,
          currentUserId: currentUserId ?? null,
          initialFirstUnreadMessageId: target.unreadMessages
            ? (target.firstUnreadMessageId ?? null)
            : null,
          isLoading: true,
          error: null,
          messages: [],
          systemMessages: [],
          chatMembers: [],
          isSendingMsgAllowed: !isAnnouncementChat(target),
          ...paginationReset,
        });

        await loadMessagesFromApi(target, set, token);
      },

      closeChat: () => {
        startNewLoad();
        set({
          isOpen: false,
          activeChat: null,
          initialFirstUnreadMessageId: null,
          messages: [],
          systemMessages: [],
          chatMembers: [],
          error: null,
          isLoading: false,
          isSending: false,
          isSendingMsgAllowed: initialState.isSendingMsgAllowed,
          ...paginationReset,
        });
      },

      loadChat: async (target: ChatTarget, currentUserId?: number) => {
        const token = startNewLoad();
        set({
          mode: 'embedded',
          activeChat: target,
          currentUserId: currentUserId ?? null,
          initialFirstUnreadMessageId: target.unreadMessages
            ? (target.firstUnreadMessageId ?? null)
            : null,
          isLoading: true,
          error: null,
          messages: [],
          systemMessages: [],
          chatMembers: [],
          isSendingMsgAllowed: !isAnnouncementChat(target),
          ...paginationReset,
        });

        await loadMessagesFromApi(target, set, token);
      },

      loadOlderMessages: async () => {
        const {
          activeChat,
          hasMoreOlder,
          isLoadingOlder,
          isLoading,
          pagination,
        } = get();

        if (
          !activeChat ||
          activeChat.chatType !== ChatType.DIRECT ||
          !hasMoreOlder ||
          isLoadingOlder ||
          isLoading
        ) {
          return;
        }

        const nextOffset = nextOlderMessagesOffset(pagination);
        if (nextOffset == null) {
          return;
        }

        const token = currentLoadToken;
        set({ isLoadingOlder: true, error: null });

        try {
          const response = await fetchDirectChatMessages(
            activeChat.jobAssignmentId,
            {
              max: DIRECT_CHAT_MESSAGE_PAGE_SIZE,
              offset: nextOffset,
            }
          );

          if (isStaleLoad(token)) return;

          const pageMessages = response.messages || [];
          const olderMapped = mapDirectMessages(pageMessages);
          const incomingMembers = collectDirectChatMembers(response);

          // Merge against the state at commit time: a send (or a status update)
          // may have landed while this older page was in flight.
          set((state) => {
            const existingIds = new Set(state.messages.map((m) => m.id));
            const nextPagination = response.pagination ?? state.pagination;

            return {
              messages: [
                ...olderMapped.filter((m) => !existingIds.has(m.id)),
                ...state.messages,
              ],
              chatMembers: mergeChatMembers(state.chatMembers, incomingMembers),
              pagination: nextPagination,
              hasMoreOlder: hasMoreOlderMessages(
                nextPagination,
                pageMessages.length
              ),
              isLoadingOlder: false,
            };
          });
        } catch (error) {
          if (isStaleLoad(token)) return;

          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to load older messages';
          set({
            error: errorMessage,
            isLoadingOlder: false,
          });
        }
      },

      sendMessage: async (text: string) => {
        const { activeChat, messages, isSendingMsgAllowed, currentUserId } =
          get();

        if (!activeChat || !isSendingMsgAllowed || !currentUserId) {
          return false;
        }

        const trimmedText = text.trim();
        if (!trimmedText) {
          return false;
        }

        // Optimistic update: add message to list immediately
        const optimisticId = Date.now();
        const optimisticMessage: IMessageData = {
          id: optimisticId,
          text: trimmedText,
          authorId: currentUserId ?? 0, // Use current user ID for "sent by me" display
          created: new Date(),
          jobId: activeChat.jobInfo?.jobId || 0,
          jobAssignmentId: activeChat.jobAssignmentId,
          readBy: [],
          sending: true,
          sent: false,
        };

        set({
          isSending: true,
          messages: [...messages, optimisticMessage],
        });

        try {
          const isDirectChat = activeChat.chatType === ChatType.DIRECT;
          const sendFn = isDirectChat ? sendDirectChatMessage : sendChatMessage;
          // For direct chats `jobAssignmentId` carries the chatId (that's what
          // DirectMessagesTab opens the chat with and how loadMessagesFromApi
          // fetches it), so direct chats send by chatId and job chats by
          // jobAssignmentId — both live on this field.
          const sendId = activeChat.jobAssignmentId;
          const response = await sendFn(sendId, {
            message: trimmedText,
          });

          if (response.success) {
            // Update optimistic message to sent
            set((state) => ({
              isSending: false,
              messages: state.messages.map((msg) =>
                msg.id === optimisticId
                  ? { ...msg, sending: false, sent: true }
                  : msg
              ),
            }));
            return true;
          }

          // Mark message as failed
          set((state) => ({
            isSending: false,
            error: response.message || 'Failed to send message',
            messages: state.messages.map((msg) =>
              msg.id === optimisticId
                ? { ...msg, sending: false, sent: false }
                : msg
            ),
          }));
          return false;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to send message';

          // Mark message as failed
          set((state) => ({
            isSending: false,
            error: errorMessage,
            messages: state.messages.map((msg) =>
              msg.id === optimisticId
                ? { ...msg, sending: false, sent: false }
                : msg
            ),
          }));
          return false;
        }
      },

      removeMessage: (messageId: number) => {
        set((state) => ({
          messages: state.messages.filter((msg) => msg.id !== messageId),
        }));
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        startNewLoad();
        set(initialState);
      },
    }),
    { name: 'chat-store' }
  )
);

export const useChatIsOpen = () => useChatStore((state) => state.isOpen);
export const useChatMessages = () => useChatStore((state) => state.messages);
export const useChatSystemMessages = () =>
  useChatStore((state) => state.systemMessages);
export const useChatMembers = () => useChatStore((state) => state.chatMembers);
export const useChatActiveChat = () =>
  useChatStore((state) => state.activeChat);
export const useChatIsLoading = () => useChatStore((state) => state.isLoading);
export const useChatIsSending = () => useChatStore((state) => state.isSending);
export const useChatError = () => useChatStore((state) => state.error);
export const useChatIsSendingAllowed = () =>
  useChatStore((state) => state.isSendingMsgAllowed);
export const useChatInitialFirstUnreadMessageId = () =>
  useChatStore((state) => state.initialFirstUnreadMessageId);
export const useChatHasMoreOlder = () =>
  useChatStore((state) => state.hasMoreOlder);
export const useChatIsLoadingOlder = () =>
  useChatStore((state) => state.isLoadingOlder);
